import { intoResultAsync } from '@app/packages/Result/Result'

import type { AuthSessionManagerInterface } from './AuthSessionManager'
import { ApiError, RequestError } from './errors'
import { assertResponseHasNoError, parseResponseBody } from './response'
import type { ApiAction, ApiDispatcherDispatchOptions, ApiDispatcherInterface } from './utils'

type FetchType = typeof fetch

export type ApiDispatcherConstructorOptions = {
  base: string
  fetch: FetchType
  getHeaders: (options?: { anonymous?: boolean }) => Record<string, string>
  authSession?: AuthSessionManagerInterface
}

export class ApiDispatcher implements ApiDispatcherInterface {
  init: ApiDispatcherConstructorOptions

  constructor(init: ApiDispatcherConstructorOptions) {
    this.init = init
  }

  dispatch = <RR>(apiAction: (dispatch: <R>(action: ApiAction) => Promise<R>) => Promise<RR>, options?: ApiDispatcherDispatchOptions): Promise<RR> => {
    return apiAction(action => this.dispatchAction(action, options))
  }

  private dispatchAction = async <R>(action: ApiAction, options?: ApiDispatcherDispatchOptions, retried = false): Promise<R> => {
    const session = options?.session
    let headers = this.init.getHeaders({ anonymous: session?.type === 'anonymous' })
    if (!action.body || typeof action.body === 'string') {
      headers['content-type'] = 'application/json'
    }
    let hadAccessToken = false
    if (session?.type === 'explicit') {
      headers['Authorization'] = `Bearer ${session.token}`
    } else if (this.init.authSession && !session) {
      const token = await this.init.authSession.ensureAccessToken({ signal: options?.abortSignal })
      if (token !== null) {
        headers['Authorization'] = `Bearer ${token}`
        hadAccessToken = true
      }
    }
    if (action.headers) {
      headers = action.headers(headers)
    }

    const respResult = await intoResultAsync(
      this.init.fetch(`${this.init.base}${action.endpoint}`, {
        method: action.method,
        headers,
        body: action.body,
        credentials: action.credentials ?? 'omit',
        redirect: 'follow',
        signal: options?.abortSignal,
        keepalive: action.keepalive,
        priority: action.priority,
      })
    )

    if (respResult.error) {
      throw new RequestError(respResult.value instanceof Error ? respResult.value.message : String(respResult.value), { cause: respResult.value })
    }

    if (!respResult.value.ok) {
      if (respResult.value.status === 401 && this.init.authSession && !retried && hadAccessToken && isReplayableBody(action.body)) {
        const refreshResult = await intoResultAsync(this.init.authSession.forceRefresh({ signal: options?.abortSignal }))
        if (refreshResult.error && !(refreshResult.value instanceof ApiError && refreshResult.value.handled)) {
          throw refreshResult.value
        }
        return this.dispatchAction(action, options, true)
      }
      await assertResponseHasNoError(respResult.value, action.method, `${this.init.base}${action.endpoint}`)
    }

    const text = await respResult.value.text()
    if (!text) return undefined as R
    return parseResponseBody(text, `${action.method} ${action.endpoint}`) as R
  }
}

const isReplayableBody = (body: ApiAction['body']): boolean => {
  return body === undefined || typeof body === 'string'
}
